import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminBulkReplayCapturedEvents,
  AdminDiscardCapturedEvent,
  AdminFetchCapturedEvents,
  AdminFetchKillswitchFlags,
  AdminReplayAllCapturedEvents,
  AdminReplayCapturedEvent,
  AdminToggleKillswitch,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { KillswitchFeature } from 'src/constants/payments-ops/enums'
import type {
  BulkReplayCapturedEventsPayload,
  BulkReplayResult,
  CapturedEvent,
  DiscardCapturedEventPayload,
  FeatureFlag,
  FetchCapturedEventsPayload,
  KillswitchFlagsByFeature,
  ReplayAllCapturedEventsPayload,
  ReplayCapturedEventPayload,
  ToggleKillswitchPayload,
} from 'src/interfaces/usBankKillswitch'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchKillswitchFlags(action: AsyncAction<undefined>) {
  try {
    // No `partnerId` on purpose: omitted, each call returns the global row AND every per-partner
    // row, which the Killswitch page needs to show partners stopped on their own. (`'null'`
    // would return the global row only.)
    const [paymentsResponse, webhooksResponse]: Array<{ payload: FeatureFlag[] }> = yield all([
      call(callSecureApi, Endpoints.AdminFetchFeatureFlags, {
        method: 'GET',
        query: { feature: KillswitchFeature.PaymentProcessing },
      }),
      call(callSecureApi, Endpoints.AdminFetchFeatureFlags, {
        method: 'GET',
        query: { feature: KillswitchFeature.Webhooks },
      }),
    ])

    const flags: KillswitchFlagsByFeature = {
      [KillswitchFeature.PaymentProcessing]: paymentsResponse?.payload ?? [],
      [KillswitchFeature.Webhooks]: webhooksResponse?.payload ?? [],
    }

    yield put(AdminFetchKillswitchFlags.success(flags))

    if (action.next) {
      action.next(null, flags)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.usBankKillswitch.onFetchKillswitchFlags] error' })

    yield put(AdminFetchKillswitchFlags.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the killswitches'))
    }
  }
}

export function* onToggleKillswitch(action: AsyncAction<ToggleKillswitchPayload>) {
  const { feature, enabled, flagId } = action.payload

  try {
    // No global row yet → create one. `partnerId: null` must be EXPLICIT: omitted, the duplicate
    // check matches any per-partner row for the feature and the create 400s.
    const { payload }: { payload: FeatureFlag } = flagId
      ? yield call(callSecureApi, Endpoints.AdminUpdateFeatureFlag, {
          method: 'PATCH',
          params: [{ field: ':flagId', value: flagId }],
          body: { enabled },
        })
      : yield call(callSecureApi, Endpoints.AdminCreateFeatureFlag, {
          method: 'POST',
          body: { feature, enabled, partnerId: null },
        })

    yield put(AdminToggleKillswitch.success(payload))
    yield put(AdminFetchKillswitchFlags.request())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.usBankKillswitch.onToggleKillswitch] error' })

    yield put(AdminToggleKillswitch.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to change the killswitch'))
    }
  }
}

export function* onFetchCapturedEvents(action: AsyncAction<FetchCapturedEventsPayload>) {
  try {
    // `limit` and `page` are `@IsNumber` without `@Type` on the backend — sending either is a
    // 400. Without them the backend returns the newest 50, with no total.
    const { payload }: { payload: CapturedEvent[] } = yield call(
      callSecureApi,
      Endpoints.AdminFetchCapturedEvents,
      {
        method: 'GET',
        query: { status: action.payload?.status, source: action.payload?.source },
      },
    )

    const events = payload ?? []

    yield put(AdminFetchCapturedEvents.success(events))

    if (action.next) {
      action.next(null, events)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.usBankKillswitch.onFetchCapturedEvents] error' })

    yield put(AdminFetchCapturedEvents.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load captured events'))
    }
  }
}

export function* onReplayCapturedEvent(action: AsyncAction<ReplayCapturedEventPayload>) {
  const { eventId, refetch } = action.payload

  try {
    // No body: the internal route takes the actor from the WorkOS token.
    const { payload } = yield call(callSecureApi, Endpoints.AdminReplayCapturedEvent, {
      method: 'POST',
      params: [{ field: ':eventId', value: eventId }],
    })

    yield put(AdminReplayCapturedEvent.success(payload))
    yield put(AdminFetchCapturedEvents.request(refetch))

    if (action.next) {
      action.next(null, payload ?? true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.usBankKillswitch.onReplayCapturedEvent] error' })

    yield put(AdminReplayCapturedEvent.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to replay the event'))
    }
  }
}

export function* onDiscardCapturedEvent(action: AsyncAction<DiscardCapturedEventPayload>) {
  const { eventId, notes, refetch } = action.payload

  try {
    const { payload } = yield call(callSecureApi, Endpoints.AdminDiscardCapturedEvent, {
      method: 'POST',
      params: [{ field: ':eventId', value: eventId }],
      body: { notes },
    })

    yield put(AdminDiscardCapturedEvent.success(payload))
    yield put(AdminFetchCapturedEvents.request(refetch))

    if (action.next) {
      action.next(null, payload ?? true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.usBankKillswitch.onDiscardCapturedEvent] error' })

    yield put(AdminDiscardCapturedEvent.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to discard the event'))
    }
  }
}

export function* onBulkReplayCapturedEvents(action: AsyncAction<BulkReplayCapturedEventsPayload>) {
  const { eventIds, refetch } = action.payload

  try {
    const { payload }: { payload: BulkReplayResult } = yield call(
      callSecureApi,
      Endpoints.AdminBulkReplayCapturedEvents,
      { method: 'POST', body: { eventIds } },
    )

    yield put(AdminBulkReplayCapturedEvents.success(payload))
    yield put(AdminFetchCapturedEvents.request(refetch))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.usBankKillswitch.onBulkReplayCapturedEvents] error',
    })

    yield put(AdminBulkReplayCapturedEvents.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to replay the selected events'))
    }
  }
}

export function* onReplayAllCapturedEvents(action: AsyncAction<ReplayAllCapturedEventsPayload>) {
  try {
    // No body — the actor comes from the token; `{ replayedBy }` is no longer read.
    const { payload }: { payload: BulkReplayResult } = yield call(
      callSecureApi,
      Endpoints.AdminReplayAllCapturedEvents,
      { method: 'POST' },
    )

    yield put(AdminReplayAllCapturedEvents.success(payload))
    yield put(AdminFetchCapturedEvents.request(action.payload?.refetch ?? {}))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.usBankKillswitch.onReplayAllCapturedEvents] error',
    })

    yield put(AdminReplayAllCapturedEvents.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to replay pending events'))
    }
  }
}

export function* usBankKillswitchSaga() {
  yield takeLatest(AdminFetchKillswitchFlags.type.REQUEST, onFetchKillswitchFlags)
  yield takeLatest(AdminFetchCapturedEvents.type.REQUEST, onFetchCapturedEvents)
  yield takeEvery(AdminToggleKillswitch.type.REQUEST, onToggleKillswitch)
  yield takeEvery(AdminReplayCapturedEvent.type.REQUEST, onReplayCapturedEvent)
  yield takeEvery(AdminDiscardCapturedEvent.type.REQUEST, onDiscardCapturedEvent)
  yield takeEvery(AdminBulkReplayCapturedEvents.type.REQUEST, onBulkReplayCapturedEvents)
  yield takeEvery(AdminReplayAllCapturedEvents.type.REQUEST, onReplayAllCapturedEvents)
}
